/**
 * InstallPrompt — one-click PWA install + auto-permission bundle.
 *
 * Big, obvious "Install app" call-to-action so lazy users don't have
 * to know about the Chrome overflow menu or Safari's Share button.
 *
 * Flow:
 *   - Android/Desktop Chrome/Edge: capture `beforeinstallprompt`, on
 *     click call `.prompt()` — one-tap install into the OS launcher.
 *     After install (`appinstalled` event) we also fire the
 *     Doserne notification enable + getUserMedia in one go so the
 *     user is 100 % set up.
 *   - iOS Safari: no programmatic install exists in WebKit, so we
 *     open a step-by-step visual modal with big arrows pointing at
 *     the Share button + "Add to Home Screen".
 *   - Already installed (display-mode: standalone or navigator.standalone)
 *     → button hides itself entirely.
 */
import React, { useEffect, useRef, useState } from "react";
import { Download, X, Share, PlusSquare, Check, Bell, Mic, Video as VideoIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

function clientId() {
  try {
    let id = localStorage.getItem("DOSERNE_INSTALL_CID");
    if (!id) {
      id = (crypto?.randomUUID && crypto.randomUUID()) ||
        `cid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("DOSERNE_INSTALL_CID", id);
    }
    return id;
  } catch { return null; }
}

function track(event, extras = {}) {
  try {
    const body = JSON.stringify({
      event,
      platform: detectPlatform(),
      is_standalone: window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true,
      lang: (localStorage.getItem("DOSERNE_LANG") || navigator.language || "").slice(0, 8),
      client_id: clientId(),
      ...extras,
    });
    // Use sendBeacon when the page is unloading, fetch otherwise —
    // both fire-and-forget so a failure never impacts UX.
    if (navigator.sendBeacon && event === "opened_standalone") {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${BACKEND}/api/doserne/install/track`, blob);
    } else {
      fetch(`${BACKEND}/api/doserne/install/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* analytics never breaks the app */ }
}

const COPY = {
  no: {
    cta: "Installer appen",
    subline: "1 klikk · varsler + samtaler klare",
    ios_title: "Installer Doserne på iPhone",
    ios_step1_title: "Trykk «Del»-knappen nederst",
    ios_step1_body: "Ser ut som en firkant med en pil ↑",
    ios_step2_title: "Rull ned og trykk «Legg til på Hjem-skjerm»",
    ios_step2_body: "Ikonet dukker opp på hjem-skjermen",
    ios_step3_title: "Trykk «Legg til» øverst til høyre",
    ios_step3_body: "Deretter åpne Doserne fra hjem-skjermen",
    ios_ok: "Skjønner",
    installing: "Installerer…",
    permissions_title: "Nesten ferdig!",
    permissions_body: "Aktiver varsler + mikrofon + kamera nå så du kan motta meldinger og samtaler.",
    permissions_ok: "Aktiver alt",
    permissions_skip: "Senere",
    already_installed: "Doserne er allerede installert",
    close: "Lukk",
  },
  ro: {
    cta: "Instalează aplicația",
    subline: "1 click · notificări + apeluri gata",
    ios_title: "Instalează Doserne pe iPhone",
    ios_step1_title: "Apasă butonul «Share» de jos",
    ios_step1_body: "Arată ca un pătrat cu săgeată ↑",
    ios_step2_title: "Derulează și apasă «Add to Home Screen»",
    ios_step2_body: "Iconița va apărea pe ecranul principal",
    ios_step3_title: "Apasă «Add» sus în dreapta",
    ios_step3_body: "Apoi deschide Doserne de pe ecranul principal",
    ios_ok: "Am înțeles",
    installing: "Se instalează…",
    permissions_title: "Aproape gata!",
    permissions_body: "Activează notificări + microfon + cameră acum ca să primești mesaje și apeluri.",
    permissions_ok: "Activează tot",
    permissions_skip: "Mai târziu",
    already_installed: "Doserne e deja instalat",
    close: "Închide",
  },
  en: {
    cta: "Install app",
    subline: "1-tap · notifications + calls ready",
    ios_title: "Install Doserne on iPhone",
    ios_step1_title: "Tap the Share button at the bottom",
    ios_step1_body: "Looks like a square with an arrow ↑",
    ios_step2_title: "Scroll down and tap «Add to Home Screen»",
    ios_step2_body: "The icon will appear on your home screen",
    ios_step3_title: "Tap «Add» at the top-right",
    ios_step3_body: "Then open Doserne from your home screen",
    ios_ok: "Got it",
    installing: "Installing…",
    permissions_title: "Almost done!",
    permissions_body: "Turn on notifications + mic + camera now so you can receive messages and calls.",
    permissions_ok: "Enable all",
    permissions_skip: "Later",
    already_installed: "Doserne is already installed",
    close: "Close",
  },
};

function pickLang() {
  try {
    const saved = localStorage.getItem("DOSERNE_LANG");
    if (saved && COPY[saved]) return saved;
  } catch { /* ignore */ }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "en").toLowerCase();
  if (nav.startsWith("ro")) return "ro";
  if (nav.startsWith("nb") || nav.startsWith("nn") || nav.startsWith("no") || nav.startsWith("da") || nav.startsWith("sv")) return "no";
  return "en";
}

export default function InstallPrompt({ compact = false, onInstalled }) {
  const [deferred, setDeferred] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [showPermissionsStep, setShowPermissionsStep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("en");
  const shownRef = useRef(false);
  const T = COPY[lang] || COPY.en;

  useEffect(() => {
    setLang(pickLang());
    const check = () => {
      const s = window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      setStandalone(s);
      if (s && !shownRef.current) {
        // Fires once per mount when the app is already installed —
        // this is our "installed" count for iOS (which has no
        // appinstalled event) plus repeat opens from the launcher.
        track("opened_standalone");
        shownRef.current = true;
      } else if (!s && !shownRef.current) {
        track("prompt_shown");
        shownRef.current = true;
      }
    };
    check();
    const mq = window.matchMedia("(display-mode: standalone)");
    mq.addEventListener?.("change", check);
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onBip);
    const onAppInstalled = () => {
      track("installed");
      setDeferred(null);
      setStandalone(true);
      // Right after install, nudge the user through the permission
      // step. Some Android launchers auto-open the installed app —
      // in that case the standalone check above already covers us.
      setShowPermissionsStep(true);
    };
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onAppInstalled);
      mq.removeEventListener?.("change", check);
    };
  }, []);

  if (standalone && !showPermissionsStep) return null;

  const isIOS = typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;

  const requestPermissions = async () => {
    setBusy(true);
    let notifOk = false;
    let mediaOk = false;
    try {
      // Notifications first — user needs to see the permission dialog.
      if ("Notification" in window && Notification.permission !== "granted") {
        try {
          const p = await Notification.requestPermission();
          notifOk = p === "granted";
        } catch { /* ignore */ }
      } else if ("Notification" in window) {
        notifOk = Notification.permission === "granted";
      }
      // Then mic + camera together so the OS shows a single combined prompt.
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        s.getTracks().forEach((t) => t.stop());
        mediaOk = true;
      } catch { /* user declined — that's OK */ }
    } catch { /* ignore */ }
    track(notifOk && mediaOk ? "permissions_granted" : "permissions_denied", {});
    setBusy(false);
    setShowPermissionsStep(false);
    onInstalled?.();
  };

  const click = async () => {
    track("install_clicked");
    if (isIOS) {
      track("ios_guide_shown");
      setShowIosGuide(true);
      return;
    }
    if (deferred) {
      setBusy(true);
      try {
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") {
          // `appinstalled` event will fire — permission step opens
          // from there. Nothing else to do here.
        }
      } catch { /* ignore */ }
      setDeferred(null);
      setBusy(false);
      return;
    }
    // No BIP fired yet (uncommon) — user probably needs to visit
    // the site a couple of times before Chrome offers install.
    // Fall back to the iOS-style guide so they still have a path.
    track("ios_guide_shown");
    setShowIosGuide(true);
  };

  // ── Main button ────────────────────────────────────────────
  const button = (
    <button
      onClick={click}
      disabled={busy}
      className={
        compact
          ? "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-xs font-black shadow active:scale-95 disabled:opacity-60"
          : "inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-sm font-black shadow-lg active:scale-95 disabled:opacity-60"
      }
      style={{ color: "#fff" }}
      data-testid="install-prompt-btn"
    >
      <Download className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
      <span className="text-left leading-tight" style={{ color: "#fff" }}>
        {busy ? T.installing : T.cta}
        {!compact && (
          <span className="block text-[10px] font-medium opacity-95" style={{ color: "#fff" }}>
            {T.subline}
          </span>
        )}
      </span>
    </button>
  );

  return (
    <>
      {!standalone && button}

      {/* iOS Add-to-Home-Screen visual guide */}
      {showIosGuide && (
        <div className="fixed inset-0 z-[85] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setShowIosGuide(false)}>
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-5 relative" onClick={(e) => e.stopPropagation()} data-testid="ios-install-guide">
            <button
              onClick={() => setShowIosGuide(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
              aria-label={T.close}
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
            <div className="text-center pb-4 border-b border-slate-100">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mx-auto shadow-lg mb-2">
                <Download className="w-7 h-7" style={{ color: "#fff" }} />
              </div>
              <div className="text-lg font-black text-slate-900">{T.ios_title}</div>
            </div>
            <div className="mt-4 space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
                  <span className="font-black text-sm">1</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-black text-slate-900">{T.ios_step1_title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{T.ios_step1_body}</div>
                </div>
                <Share className="w-6 h-6 text-blue-500 flex-shrink-0" />
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
                  <span className="font-black text-sm">2</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-black text-slate-900">{T.ios_step2_title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{T.ios_step2_body}</div>
                </div>
                <PlusSquare className="w-6 h-6 text-slate-500 flex-shrink-0" />
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
                  <span className="font-black text-sm">3</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-black text-slate-900">{T.ios_step3_title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{T.ios_step3_body}</div>
                </div>
                <Check className="w-6 h-6 text-emerald-500 flex-shrink-0" />
              </div>
            </div>
            <button
              onClick={() => setShowIosGuide(false)}
              className="w-full mt-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-sm font-black active:scale-95"
              style={{ color: "#fff" }}
              data-testid="ios-install-ok"
            >
              {T.ios_ok}
            </button>
          </div>
        </div>
      )}

      {/* Post-install permission step */}
      {showPermissionsStep && (
        <div className="fixed inset-0 z-[85] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-5" data-testid="install-permissions-step">
            <div className="text-center pb-4 border-b border-slate-100">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mx-auto shadow-lg mb-2">
                <Check className="w-7 h-7" style={{ color: "#fff" }} />
              </div>
              <div className="text-lg font-black text-slate-900">{T.permissions_title}</div>
              <div className="text-sm text-slate-600 mt-1">{T.permissions_body}</div>
            </div>
            <div className="mt-4 flex justify-center gap-6">
              <div className="flex flex-col items-center gap-1"><div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"><Bell className="w-5 h-5 text-slate-700" /></div><div className="text-[10px] font-bold text-slate-600">Varsler</div></div>
              <div className="flex flex-col items-center gap-1"><div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"><Mic className="w-5 h-5 text-slate-700" /></div><div className="text-[10px] font-bold text-slate-600">Mikrofon</div></div>
              <div className="flex flex-col items-center gap-1"><div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"><VideoIcon className="w-5 h-5 text-slate-700" /></div><div className="text-[10px] font-bold text-slate-600">Kamera</div></div>
            </div>
            <div className="mt-5 space-y-2">
              <button
                onClick={requestPermissions}
                disabled={busy}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-sm font-black shadow active:scale-95 disabled:opacity-60"
                style={{ color: "#fff" }}
                data-testid="install-permissions-ok"
              >
                {busy ? T.installing : T.permissions_ok}
              </button>
              <button
                onClick={() => setShowPermissionsStep(false)}
                className="w-full py-2 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-900"
                data-testid="install-permissions-skip"
              >
                {T.permissions_skip}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
